import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Star, ShieldCheck, Award, Clock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { usePhoneBar } from "@/contexts/PhoneBarContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import safewayLogoColor from "@/assets/safeway-logo-color.png";
import gafBadge from "@/assets/gaf-certified-badge.png";

export const AdsQuizHero = () => {
  const { phoneNumber } = usePhoneBar();
  const { toast } = useToast();
  const formRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0); // 0=initial, 1=zip, 2=details
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ zip: "", name: "", phone: "", email: "" });
  const [countdown, setCountdown] = useState({ hours: 2, minutes: 14, seconds: 37 });

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) return { hours: 0, minutes: 0, seconds: 0 };
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleZipSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.zip.length >= 5) setStep(2);
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-contact-form", {
        body: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: "residential-roofing",
          message: `Zip: ${formData.zip} — From quiz landing page`,
        },
      });
      if (error) throw error;
      toast({ title: "Thank You!", description: "We'll contact you shortly for your free estimate." });
      setFormData({ zip: "", name: "", phone: "", email: "" });
      setStep(0);
    } catch {
      toast({ title: "Error", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    setStep(1);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const formattedPhone = phoneNumber.replace("+1", "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <section className="relative bg-background">
      {/* Seasonal Banner */}
      <div className="bg-primary text-primary-foreground text-center py-2 px-4">
        <p className="text-sm font-bold tracking-wide uppercase">
          🏠 Spring Roofing Event — Save Up To $2,500 — Limited Time Only
        </p>
      </div>

      <div className="container mx-auto px-4 py-10 md:py-16 text-center max-w-3xl">
        {/* Logo */}
        <img src={safewayLogoColor} alt="Safeway Roofing" className="h-14 md:h-16 w-auto mx-auto mb-6" />

        {/* Stars */}
        <div className="flex items-center justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-secondary text-secondary" />
          ))}
          <span className="text-sm text-muted-foreground font-medium ml-2">4.9 Stars · 500+ Reviews</span>
        </div>

        {/* Headline */}
        <h1 className="text-3xl md:text-5xl font-extrabold text-foreground leading-tight mb-4">
          Save Thousands On<br />
          <span className="text-primary">A New Roof</span>
        </h1>
        <p className="text-muted-foreground text-base md:text-lg mb-8 max-w-xl mx-auto">
          Long Island's top-rated roofing contractor. GAF Master Elite® certified — top 2% of roofers nationwide.
        </p>

        {/* Trust icons row */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
          {[
            { icon: ShieldCheck, label: "GAF Master Elite" },
            { icon: Award, label: "Lifetime Warranty" },
            { icon: CheckCircle2, label: "Licensed & Insured" },
            { icon: Clock, label: "Since 2007" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Icon className="w-4 h-4 text-primary" />
              <span className="font-medium">{label}</span>
            </div>
          ))}
        </div>

        {/* Discount Card */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 mb-8 max-w-md mx-auto shadow-lg">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src={gafBadge} alt="GAF Certified" className="h-12 w-auto" />
            <div className="text-left">
              <p className="text-2xl font-extrabold">$500 OFF</p>
              <p className="text-sm opacity-90">+ 0% Financing for 18 Months</p>
            </div>
          </div>
          <p className="text-xs opacity-75 mt-2">Cannot be combined with other offers. Expires soon.</p>
        </div>

        {/* CTA / Form */}
        <div ref={formRef}>
          {step === 0 && (
            <Button
              size="lg"
              onClick={scrollToForm}
              className="h-14 px-10 text-lg font-extrabold rounded-full shadow-xl animate-pulse hover:animate-none"
            >
              CHECK MY ZIP CODE <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
          {step === 1 && (
            <form onSubmit={handleZipSubmit} className="max-w-sm mx-auto space-y-3">
              <label className="text-sm font-semibold text-foreground">Enter your zip code</label>
              <div className="flex gap-2">
                <Input
                  placeholder="ZIP Code"
                  value={formData.zip}
                  onChange={(e) => setFormData((p) => ({ ...p, zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                  className="text-lg h-12"
                  required minLength={5} maxLength={5} inputMode="numeric" autoFocus
                />
                <Button type="submit" size="lg" className="h-12 px-6 font-bold">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </form>
          )}
          {step === 2 && (
            <form onSubmit={handleFinalSubmit} className="max-w-sm mx-auto space-y-3">
              <p className="text-sm text-muted-foreground">
                ZIP: <strong>{formData.zip}</strong>{" "}
                <button type="button" onClick={() => setStep(1)} className="text-primary underline text-xs">Change</button>
              </p>
              <Input placeholder="Full Name" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} required className="h-11" />
              <Input placeholder="Phone Number" type="tel" value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} required className="h-11" />
              <Input placeholder="Email Address" type="email" value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} required className="h-11" />
              <Button type="submit" disabled={isSubmitting} className="w-full h-12 font-bold text-base">
                {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Get My Free Estimate"}
              </Button>
            </form>
          )}
        </div>

        {/* Countdown */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4 text-primary" />
          <span>Offer expires in</span>
          <span className="font-mono font-bold text-foreground">
            {pad(countdown.hours)}:{pad(countdown.minutes)}:{pad(countdown.seconds)}
          </span>
        </div>

        {/* Phone */}
        <p className="text-xs text-muted-foreground mt-4">
          Or call now:{" "}
          <a href={`tel:${phoneNumber}`} className="text-primary font-bold hover:underline">{formattedPhone}</a>
        </p>
      </div>
    </section>
  );
};
